import { Button } from "@heroui/react";
import {
	type AdminGoldenAsset,
	type AdminSeriesDetail,
	type CreateCharacterInput,
	type CreateLocationInput,
	type GenerationJob,
	type GenerationTargetType,
	GoldenAssetKind,
	GoldenAssetOwnerType,
	GenerationTargetType as TargetType,
	type UpdateCharacterInput,
	type UpdateLocationInput,
	type UpsertSeriesBibleInput,
} from "@storyline/shared";
import { useState } from "react";
import SectionCard from "@/src/common/components/section-card";
import AssetRow from "@/src/series/components/asset-row";
import BibleForm from "@/src/series/components/bible-form";
import CharacterCard from "@/src/series/components/character-card";
import CharacterForm from "@/src/series/components/character-form";
import LocationCard from "@/src/series/components/location-card";
import LocationForm from "@/src/series/components/location-form";
import { generatingAssetKeys } from "@/src/series/utils/asset-slots";

const SERIES_KINDS = [GoldenAssetKind.STYLE_REFERENCE, GoldenAssetKind.POV_HANDS];

type BibleStudioProps = {
	detail: AdminSeriesDetail;
	assets: AdminGoldenAsset[];
	generations: GenerationJob[];
	onSaveBible: (body: UpsertSeriesBibleInput) => Promise<void>;
	onToggleBibleLock: (locked: boolean) => Promise<void>;
	onAddCharacter: (body: CreateCharacterInput) => Promise<void>;
	onEditCharacter: (characterId: string, body: UpdateCharacterInput) => Promise<void>;
	onRemoveCharacter: (characterId: string) => Promise<void>;
	onAddLocation: (body: CreateLocationInput) => Promise<void>;
	onEditLocation: (locationId: string, body: UpdateLocationInput) => Promise<void>;
	onRemoveLocation: (locationId: string) => Promise<void>;
	onGenerate: (targetType: GenerationTargetType, targetId: string, kind: GoldenAssetKind) => Promise<void>;
	onApprove: (assetId: string) => Promise<void>;
	onLock: (assetId: string) => Promise<void>;
	onUnlock: (assetId: string) => Promise<void>;
};

const BibleStudio: React.FC<BibleStudioProps> = (props) => {
	const { detail, assets, generations } = props;
	const generatingKeys = generatingAssetKeys(generations);
	const [addingCharacter, setAddingCharacter] = useState(false);
	const [addingLocation, setAddingLocation] = useState(false);

	return (
		<div className="flex flex-col gap-6">
			<SectionCard
				title="Bible visuelle & narrative"
				description="Style, ton et univers de la série. Verrouillez-la pour figer la direction."
			>
				<BibleForm bible={detail.bible} onSave={props.onSaveBible} onToggleLock={props.onToggleBibleLock} />
			</SectionCard>

			<SectionCard title="Références de série" description="Référence de style et kit POV, injectés dans toutes les générations.">
				<AssetRow
					ownerType={GoldenAssetOwnerType.SERIES}
					ownerId={null}
					targetType={TargetType.SERIES}
					targetId={detail.id}
					kinds={SERIES_KINDS}
					assets={assets}
					generatingKeys={generatingKeys}
					onGenerate={props.onGenerate}
					onApprove={props.onApprove}
					onLock={props.onLock}
					onUnlock={props.onUnlock}
				/>
			</SectionCard>

			<SectionCard
				title={`Personnages (${detail.characters.length})`}
				description="Visage officiel, angles et tenues — approuvez le visage avant de produire un épisode."
				actions={
					<Button size="sm" onPress={() => setAddingCharacter((previous) => !previous)}>
						{addingCharacter ? "Fermer" : "Ajouter"}
					</Button>
				}
			>
				<div className="flex flex-col gap-4">
					{addingCharacter ? (
						<CharacterForm
							submitLabel="Créer le personnage"
							onSubmit={async (body) => {
								await props.onAddCharacter(body);
								setAddingCharacter(false);
							}}
							onCancel={() => setAddingCharacter(false)}
						/>
					) : null}
					{detail.characters.length === 0 ? (
						<p className="text-muted text-sm">Aucun personnage pour l'instant.</p>
					) : (
						detail.characters.map((character) => (
							<CharacterCard
								key={character.id}
								character={character}
								assets={assets}
								generatingKeys={generatingKeys}
								onEdit={props.onEditCharacter}
								onRemove={props.onRemoveCharacter}
								onGenerate={props.onGenerate}
								onApprove={props.onApprove}
								onLock={props.onLock}
								onUnlock={props.onUnlock}
							/>
						))
					)}
				</div>
			</SectionCard>

			<SectionCard
				title={`Lieux (${detail.locations.length})`}
				description="Plan maître et angles — approuvez le plan maître de chaque lieu utilisé avant production."
				actions={
					<Button size="sm" onPress={() => setAddingLocation((previous) => !previous)}>
						{addingLocation ? "Fermer" : "Ajouter"}
					</Button>
				}
			>
				<div className="flex flex-col gap-4">
					{addingLocation ? (
						<LocationForm
							submitLabel="Créer le lieu"
							onSubmit={async (body) => {
								await props.onAddLocation(body);
								setAddingLocation(false);
							}}
							onCancel={() => setAddingLocation(false)}
						/>
					) : null}
					{detail.locations.length === 0 ? (
						<p className="text-muted text-sm">Aucun lieu pour l'instant.</p>
					) : (
						detail.locations.map((location) => (
							<LocationCard
								key={location.id}
								location={location}
								assets={assets}
								generatingKeys={generatingKeys}
								onEdit={props.onEditLocation}
								onRemove={props.onRemoveLocation}
								onGenerate={props.onGenerate}
								onApprove={props.onApprove}
								onLock={props.onLock}
								onUnlock={props.onUnlock}
							/>
						))
					)}
				</div>
			</SectionCard>
		</div>
	);
};

export default BibleStudio;
